import clsx from "clsx";
import {
  Ban,
  PackageCheck,
  PackageOpen,
  PackageSearch,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import type { CaseStatus } from "../../shared/domain.js";
import { useControlSize, type ControlSize } from "./Button.js";

/**
 * Case status as icon + label + colour — never colour alone.
 *
 * The badge accepts only what the server already decided: the derived
 * `status` and the `needsAttention` flag from the case DTO. It performs no
 * status calculation of its own.
 */

/** The canonical `CaseStatus` union (`src/shared/domain.ts`), re-exported for client code. */
export type { CaseStatus };

export type StatusTone = CaseStatus | "needs_attention";

export type StatusPresentation = Readonly<{
  label: string;
  Icon: LucideIcon;
}>;

export const STATUS_PRESENTATION: Readonly<
  Record<StatusTone, StatusPresentation>
> = {
  ordered: { label: "Ordered", Icon: PackageSearch },
  partially_arrived: { label: "Partially arrived", Icon: PackageOpen },
  arrived: { label: "Arrived", Icon: PackageCheck },
  cancelled: { label: "Cancelled", Icon: Ban },
  needs_attention: { label: "Needs attention", Icon: TriangleAlert },
};

/**
 * Picks the one tone to show. An unresolved exception outranks the normal
 * status because it is what blocks Arrived; a cancelled case is never shown as
 * needing attention because nothing more can happen to it.
 */
export function resolveStatusTone(
  status: CaseStatus,
  needsAttention: boolean,
): StatusTone {
  if (needsAttention && status !== "cancelled") return "needs_attention";
  return status;
}

export interface StatusBadgeProps {
  status: CaseStatus;
  needsAttention?: boolean;
  size?: ControlSize;
  className?: string;
}

export function StatusBadge({
  status,
  needsAttention = false,
  size,
  className,
}: StatusBadgeProps) {
  const resolvedSize = useControlSize(size);
  const tone = resolveStatusTone(status, needsAttention);
  const { label, Icon } = STATUS_PRESENTATION[tone];

  return (
    <span
      className={clsx("status-badge", className)}
      data-tone={tone}
      data-status={status}
      data-size={resolvedSize}
    >
      <Icon
        className="status-badge__icon"
        aria-hidden="true"
        focusable="false"
      />
      <span className="status-badge__label">{label}</span>
    </span>
  );
}
